/**
 * Card.tsx — render a single Wonders card. Reuses the deckplanet card
 * art served from R2; overlays counter chips, tap rotation, face-down
 * back. Draggable via dnd-kit.
 *
 * Right-click opens a context menu (passed in by the parent so menu
 * actions can dispatch TableActions on the parent's reducer).
 */

import { useDndContext, useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CardData, Placement } from '../types.js';
import { CardTooltip } from './CardTooltip.js';

export type CardProps = {
  cardId: string;
  card: CardData | undefined;
  imageUrl: string;
  /** When the card is on the board, this is its placement state. */
  placement?: Placement | undefined;
  /** Drag-id key — must be unique within a DndContext. */
  draggableId?: string | undefined;
  /** Drag payload exposed to drop targets. */
  dragData?: Record<string, unknown> | undefined;
  /** When `true`, render face-down (back). */
  faceDown?: boolean | undefined;
  /** When `true`, apply tap rotation. */
  tapped?: boolean | undefined;
  onContextMenu?: ((e: React.MouseEvent) => void) | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
  /** Override visual size; defaults to "md". */
  size?: 'sm' | 'md' | 'lg' | undefined;
};

/**
 * Counter-name → asset path map. Keys match the canonical counter kinds
 * the table-plugin emits via AddCounter / RemoveCounter. CotS card text
 * may introduce additional counter kinds at runtime; those fall through
 * to the text-badge code path below.
 */
const COUNTER_ICONS: Record<string, string> = {
  '+1': '/assets/wonders/counter-plus-one.png',
  '-1': '/assets/wonders/counter-minus-one.png',
  shield: '/assets/wonders/counter-shield.png',
  protector: '/assets/wonders/counter-protector.png',
  growth: '/assets/wonders/counter-growth.png',
  // CotS-added stone counter — reuse the realm-specific stone image for
  // the controlling player's realm at the call site; here we surface a
  // generic stone as a fallback indicator if a placement carries this
  // counter on a non-stone-item card (rare but harmless).
  stone: '/assets/wonders/stone-clear.png',
};

// Sizes bumped 2026-05-06 — testers reported board cards too small to
// read art, hand cramped. ~33% larger across the board; lg now reserved
// for board placements (was unused).
const SIZE_CLASS: Record<NonNullable<CardProps['size']>, string> = {
  sm: 'w-20 h-28',
  md: 'w-32 h-44',
  lg: 'w-44 h-60',
};

export function Card({
  cardId,
  card,
  imageUrl,
  placement,
  draggableId,
  dragData,
  faceDown,
  tapped,
  onContextMenu,
  onClick,
  className,
  size = 'md',
}: CardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: draggableId ?? `card-${cardId}`,
    data: dragData ?? { cardId },
    disabled: !draggableId, // when no draggableId provided, card is not draggable
  });
  // Track whether ANY drag is active (not just this card's). When a
  // drag is in flight we skip hover-tooltip setState — every card the
  // cursor crosses would otherwise trigger a re-render and starve the
  // drag's animation frames.
  const dnd = useDndContext();
  const anyDragActive = dnd.active !== null;

  // Hover-tooltip + click-to-zoom anchors. The tooltip uses the card's
  // bounding rect so CardTooltip can flip left/right to fit. The zoom
  // overlay is a portal-rendered modal; click anywhere or press Esc to
  // close. Face-down cards skip both (no info to reveal).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (!zoomed) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setZoomed(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomed]);

  // Drop any pending hover-tooltip the moment a drag starts — otherwise
  // the tooltip mounts mid-drag and adds layout work to the animation
  // frame.
  useEffect(() => {
    if (anyDragActive && hoverRect) setHoverRect(null);
  }, [anyDragActive, hoverRect]);

  const isTapped = tapped ?? placement?.tapped ?? false;
  const isFaceDown = faceDown ?? placement?.faceDown ?? false;
  // Compose drag offset + tap rotation into a single inline transform so
  // dnd-kit can update transform per frame WITHOUT the .card-tapped CSS
  // class clobbering it (and vice versa). Removing the .card-tapped
  // class entirely from the className list below.
  const dragStyle: CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)${isTapped ? ' rotate(90deg)' : ''}`,
        zIndex: 50,
      }
    : isTapped
      ? { transform: 'rotate(90deg)' }
      : undefined;
  const counters = placement?.counters ?? {};
  const counterEntries = Object.entries(counters).filter(([, n]) => n > 0);

  // Compose dnd-kit's setNodeRef with our own containerRef so we can
  // grab a DOM rect for the hover tooltip and zoom overlay.
  const composedRef = (node: HTMLDivElement | null): void => {
    setNodeRef(node);
    containerRef.current = node;
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: dnd-kit attaches keyboard listeners via {...attributes}; the div is the drag target and cannot be a button (drag would conflict with Enter/Space activation).
    // biome-ignore lint/a11y/useKeyWithClickEvents: same — keyboard interaction is provided by dnd-kit's attributes spread.
    <div
      ref={composedRef}
      style={dragStyle}
      {...listeners}
      {...attributes}
      onContextMenu={onContextMenu}
      onClick={(e) => {
        if (onClick) {
          onClick();
          return;
        }
        // Default click behaviour: open the zoom overlay for face-up
        // cards with metadata. Stop propagation so the parent zone
        // (e.g. realm tile) doesn't get a stray click.
        if (!isFaceDown && card) {
          e.stopPropagation();
          setZoomed(true);
        }
      }}
      onMouseEnter={(e) => {
        if (isFaceDown || !card) return;
        if (anyDragActive) return;
        setHoverRect((e.currentTarget as HTMLDivElement).getBoundingClientRect());
      }}
      onMouseLeave={() => setHoverRect(null)}
      className={clsx(
        'card-tappable relative rounded-card overflow-hidden select-none cursor-grab active:cursor-grabbing',
        'shadow-md shadow-black/40 ring-1 ring-cosmos-700',
        SIZE_CLASS[size],
        // .card-tapped class removed — rotation is composed into dragStyle.
        isDragging && 'opacity-60',
        className,
      )}
      title={card?.name ?? cardId}
    >
      {isFaceDown ? (
        <div className="w-full h-full bg-gradient-to-br from-cosmos-700 to-cosmos-900 flex items-center justify-center">
          <span className="font-display text-2xl text-cosmos-50/40">★</span>
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={card?.name ?? cardId}
          loading="lazy"
          draggable={false}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to text-tile when image fails to load.
            const el = e.currentTarget;
            el.style.display = 'none';
            const parent = el.parentElement;
            if (parent && !parent.dataset.fallback) {
              parent.dataset.fallback = '1';
              const div = document.createElement('div');
              div.className =
                'w-full h-full p-1 text-[8px] leading-tight bg-cosmos-700 text-cosmos-50 overflow-hidden';
              div.textContent = card?.name ?? cardId;
              parent.appendChild(div);
            }
          }}
        />
      )}

      {/* Counter chips overlay — uses generated PNG art for the five canonical
          counters; falls back to a text badge for any other counter kind a
          card may emit (CotS adds custom kinds). */}
      {counterEntries.length > 0 && (
        <div className="absolute bottom-1 left-1 flex flex-wrap gap-1">
          <AnimatePresence>
            {counterEntries.map(([kind, n]) => {
              const iconUrl = COUNTER_ICONS[kind];
              return iconUrl ? (
                <motion.span
                  key={kind}
                  className="relative flex h-12 w-12 items-center justify-center"
                  title={`${kind} ×${n}`}
                  initial={{ scale: 0, rotate: -10 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                >
                  <img
                    src={iconUrl}
                    alt={kind}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-500/60"
                    draggable={false}
                  />
                  <motion.span
                    key={`${kind}-${n}`}
                    initial={{ scale: 1.6 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.18 }}
                    className="-right-1 -bottom-1 absolute rounded bg-cosmos-950 px-1.5 py-0.5 font-bold font-mono text-[14px] text-amber-200 ring-1 ring-amber-600/70"
                  >
                    {n}
                  </motion.span>
                </motion.span>
              ) : (
                <motion.span
                  key={kind}
                  className="rounded bg-amber-500 px-2 py-1 font-bold text-[13px] text-cosmos-950"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                >
                  {kind} ×{n}
                </motion.span>
              );
            })}
          </AnimatePresence>
        </div>
      )}
      {hoverRect && card && !isFaceDown && !zoomed && !isDragging && (
        <CardTooltip card={card} imageUrl={imageUrl} anchorRect={hoverRect} />
      )}
      {zoomed &&
        card &&
        !isFaceDown &&
        createPortal(
          <CardZoom card={card} imageUrl={imageUrl} onClose={() => setZoomed(false)} />,
          document.body,
        )}
    </div>
  );
}

/**
 * CardZoom — full-screen overlay showing a large preview of the card
 * art plus its raw rules text. Click the backdrop or press Esc (handled
 * by the parent's keydown listener) to close.
 */
function CardZoom({
  card,
  imageUrl,
  onClose,
}: {
  card: CardData;
  imageUrl: string;
  onClose: () => void;
}) {
  return (
    <button
      type="button"
      className="fixed inset-0 z-[200] flex items-center justify-center gap-6 bg-cosmos-950/85 p-6 backdrop-blur-sm"
      onClick={onClose}
      aria-label="Close card preview"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: this inner div absorbs clicks; Esc is handled by the Card-level listener. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: container intentionally non-interactive — see button parent. */}
      <div
        className="flex max-w-[92vw] items-center gap-6"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        role="presentation"
      >
        <img
          src={imageUrl}
          alt={card.name}
          className="h-[80vh] max-h-[640px] w-auto rounded-card shadow-2xl ring-1 ring-cosmos-600"
          draggable={false}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        <div className="max-w-md rounded-card bg-cosmos-900 p-5 ring-1 ring-cosmos-700">
          <h3 className="font-display text-2xl text-cosmos-50">{card.name}</h3>
          <div className="mt-1 mb-4 flex gap-2 text-cosmos-400 text-xs">
            <span>{card.set_id}</span>
            <span>·</span>
            <span>{card.eval_status}</span>
          </div>
          <p className="whitespace-pre-line text-cosmos-100">
            {card.raw_text || '(no rules text)'}
          </p>
          <div className="mt-4 text-cosmos-500 text-xs">click anywhere or press Esc to close</div>
        </div>
      </div>
    </button>
  );
}
